import { AbsoluteCenter, Box, Button, Flex, HStack, Icon, Menu, Spacer, Text, chakra } from '@chakra-ui/react';
import { useLocation, useNavigate, useParams } from '@tanstack/react-router';
import { t } from 'i18next';
import React from 'react';
import { FiArrowLeft, FiBarChart2, FiCalendar, FiGrid } from 'react-icons/fi';
import { MdOutlineKeyboardArrowDown } from 'react-icons/md';

import { generatePeriodMonths } from '@/utils';

import CollecteCreateDialog from './collecte/CollecteCreateDialog';
import IndicatorAddDialog from './index/IndicatorAddDialog';

type NavItem = {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  active?: boolean;
};

export const IndicatorHeader = () => {
  const [isButtonHover, setIsButtonHover] = React.useState(false);
  const { slug, year, period } = useParams({ strict: false });
  const navigate = useNavigate();
  const location = useLocation();
  const _period = period?.replace(/\d+/g, '') || 'year';
  const _period_number = Number(period?.replace(/\D+/g, '')) || 1;

  const items: NavItem[] = [
    {
      id: 'indicators',
      title: 'Liste des indicateurs',
      description: 'Voir tous vos indicateurs RSE',
      icon: FiBarChart2,
      active: location.pathname.endsWith('/indicators'),
    },
    {
      id: 'indicators/collecte',
      title: 'Années de collecte',
      description: 'Gérer vos périodes de collecte',
      icon: FiCalendar,
      active: location.pathname.includes('/indicators/collecte'),
    },
    {
      id: 'indicators/per-year',
      title: 'Indicateurs par année',
      description: 'Voir tous vos indicateurs par année',
      icon: FiGrid,
      active: location.pathname.includes('/indicators/per-year'),
    },
  ];
  const currentNavItem = items.find((item) => item.active);
  const redirect = (id: string) => {
    navigate({
      to: `/$slug/${id}`,
      params: { slug },
    });
  };

  const goBack = () => {
    if (location.pathname.includes('/indicators/collecte') && year) {
      navigate({ to: '/$slug/indicators/collecte', params: { slug } });
    } else {
      navigate({ to: `/$slug/indicators`, params: { slug } });
    }
  };

  return (
    <Box px={8} w={'full'} shadow="lg" pos={'relative'} minH={'72px'} zIndex={2}>
      <AbsoluteCenter axis="vertical" w={'calc(100% - var(--r3-spacing-12))'}>
        <Box ml={{ base: 10, md: 0 }}>
          <Menu.Root>
            <Menu.Trigger
              asChild
              onMouseEnter={() => setIsButtonHover(true)}
              onMouseLeave={() => setIsButtonHover(false)}
            >
              <Button variant="ghost" px={2} py={6} borderRadius="md" _hover={{ bg: 'teal.50' }}>
                <Box bg={'teal.50'} borderRadius="md" p={2}>
                  <Icon as={currentNavItem?.icon} boxSize={5} color="teal.600" />
                </Box>
                <Flex direction="column" alignItems="flex-start" {...(isButtonHover && { color: 'white' })}>
                  <Text fontWeight={600} fontSize="sm">
                    {currentNavItem?.title}
                  </Text>
                  <Text fontSize="xs" color={isButtonHover ? 'white' : 'gray.500'}>
                    {currentNavItem?.description}
                  </Text>
                </Flex>
                <Icon as={MdOutlineKeyboardArrowDown} boxSize={5} ml="5" color={isButtonHover ? 'white' : 'gray.600'} />
              </Button>
            </Menu.Trigger>

            <Menu.Positioner>
              <Menu.Content p={2} borderRadius="md" boxShadow="lg" width="320px">
                <Box px={3} py={2} fontSize="xs" color="gray.500" fontWeight={600}>
                  NAVIGATION
                </Box>
                {items.map((it) => (
                  <Menu.Item key={it.id} value={it.id} onClick={() => redirect(it.id)}>
                    <HStack gap={3} width="100%" px={2} py={2} borderRadius="md">
                      <Box bg={it.active ? 'teal.50' : 'transparent'} borderRadius="md" p={2}>
                        <Icon as={it.icon} boxSize={5} color={it.active ? 'teal.600' : 'gray.600'} />
                      </Box>

                      <Box flex="1">
                        <Flex align="center">
                          <Box>
                            <Text fontSize="sm" fontWeight={600}>
                              {it.title}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {it.description}
                            </Text>
                          </Box>
                          <Spacer />

                          {it.active && (
                            <chakra.span
                              aria-hidden
                              display="inline-block"
                              ml={2}
                              boxSize="8px"
                              bg="teal.400"
                              borderRadius="full"
                            />
                          )}
                        </Flex>
                      </Box>
                    </HStack>
                  </Menu.Item>
                ))}
                <Menu.Arrow />
              </Menu.Content>
            </Menu.Positioner>
          </Menu.Root>
        </Box>
        {year && (
          <Box mx={4}>
            <Flex direction="column" alignItems="flex-start">
              <Text fontWeight={600} fontSize="sm">
                {location.pathname.endsWith('/manage') && 'PARAMÉTRAGE '}
                {year}
              </Text>
              <Text fontSize="xs" color={'gray.500'}>
                {generatePeriodMonths(_period_number, _period)}
              </Text>
            </Flex>
          </Box>
        )}
        <Spacer />
        {location.pathname.endsWith('/indicators') && <IndicatorAddDialog />}
        {location.pathname.endsWith('/indicators/collecte') && <CollecteCreateDialog />}
        {year && (
          <Button variant="outline" size="sm" onClick={goBack}>
            <Icon as={FiArrowLeft} />
            {t('ui.back')}
          </Button>
        )}
      </AbsoluteCenter>
    </Box>
  );
};

export default IndicatorHeader;
